import { ListingItem } from '@deckee/api-app-main';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { AppAsyncThunkAPI } from '../..';

import { selectCurrentLocation } from '../geo-service';
import { VesselType } from '../vessels/models';
import api from './api';
import {
  MarketplaceCategory,
  MarketplaceListingItem,
  MarketplaceListings,
  MarketplaceSafetyItem,
} from './models';
import {
  getSelectedCategory,
  selectAllMarketplaceCategories,
} from './selectors';

const findCatForListingItem = (
  listingItem: ListingItem,
  categories: MarketplaceCategory[],
): MarketplaceCategory | undefined =>
  categories.find((c) => c.markerTypes.includes(listingItem.markerType));

const transformListingItemToMarketplaceListingItem = (
  listingItem: ListingItem,
  categories: MarketplaceCategory[],
): MarketplaceListingItem => {
  const category = findCatForListingItem(listingItem, categories);

  const image = listingItem.image
    ? `/public/app-main/image/${listingItem.image}`
    : null;
  let coverImage = category?.image || undefined;
  if (listingItem.partner && image) coverImage = image;

  const { rating, ratingsTotal } = listingItem;
  let starRating;
  if ((rating || rating === 0) && ratingsTotal && ratingsTotal > 0) {
    starRating = rating > 0 ? (rating * 5).toFixed(1) : '0';
  }

  const marketplaceListingItem: MarketplaceListingItem = {
    ...listingItem,
    image,
    imageId: listingItem.image || undefined,
    coverImage,
    starRating,
  };
  return marketplaceListingItem;
};

export const reset = createAction('[Marketplace] Reset marketplace');

export const resetMarketplaceSearch = createAction(
  '[Marketplace] Reset marketplace search',
);

export const resetMarketplaceSelectedListing = createAction(
  '[Marketplace] Reset marketplace selected listing',
);

export const setSelectedCategory = createAction(
  '[Marketplace] Set selected marketplace category',
  (id: string) => ({ payload: id }),
);

export const setSelectedListing = createAction(
  '[Marketplace] Set selected marketplace listing',
  (id: string) => ({ payload: id }),
);

export const clearSelectedCategory = createAction(
  '[Marketplace] Clear selected marketplace category',
);

export const setMarketplaceSearchResults = createAction(
  '[Marketplace] Set marketplace search results',
  (results: MarketplaceListings) => ({ payload: results }),
);

export const updateMarketplaceListings = createAction(
  '[Marketplace] Update marketplace listings',
  (results: MarketplaceListings) => ({ payload: results }),
);

export const fetchMarketplaceListingById = createAsyncThunk<
  void,
  string,
  AppAsyncThunkAPI
>(
  '[Marketplace] Fetch marketplace listing',
  async (id, { rejectWithValue, dispatch, getState }) => {
    dispatch(setSelectedListing(id));
    const currentPosition = selectCurrentLocation(getState());

    try {
      const { data } = await api.getMarketplaceListing({
        id,
        latitude: currentPosition.latitude,
        longitude: currentPosition.longitude,
      });

      dispatch(
        updateMarketplaceListings([
          transformListingItemToMarketplaceListingItem(
            data,
            selectAllMarketplaceCategories(getState()),
          ),
        ]),
      );
    } catch (e: any) {
      rejectWithValue('Could not load marketplace listing');
    }
  },
);

export const setMarketplaceCategories = createAction(
  '[Marketplace] set categories',
  (categories: MarketplaceCategory[]) => ({
    payload: categories,
  }),
);

export const claimBusinessListing = createAsyncThunk<
  void,
  string,
  AppAsyncThunkAPI
>('[Marketplace] claim business listing', async (id) => {
  // Request the listing claim
  await api.claimListing({
    id,
  });
});

export const fetchMarketplaceCategories = createAsyncThunk<
  void,
  void,
  AppAsyncThunkAPI
>(
  '[Marketplace] fetch categories',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await api.getMarketplaceCategories();
      dispatch(setMarketplaceCategories(data));
    } catch (e: any) {
      rejectWithValue(e.message);
    }
  },
);

export const setMarketplaceSafetyItems = createAction(
  '[Marketplace] set safety items',
  (items: MarketplaceSafetyItem[]) => ({
    payload: items,
  }),
);

export const fetchMarketplaceSafetyItems = createAsyncThunk<
  void,
  {
    areaWaterBodyKey: string;
    vesselLength?: string;
    vesselType: VesselType;
  },
  AppAsyncThunkAPI
>(
  '[Marketplace] Fetch marketplace safety items',
  async (
    { areaWaterBodyKey, vesselType, vesselLength },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { data } = await api.getSafetyItems({
        areaWaterBodyKey,
        vesselType,
        vesselLength,
      });
      dispatch(setMarketplaceSafetyItems(data));
    } catch (e: any) {
      rejectWithValue(e.message);
    }
  },
);

export const searchMarketListings = createAsyncThunk<
  void,
  void,
  AppAsyncThunkAPI
>(
  '[Marketplace] search market listings',
  async (_, { rejectWithValue, getState, dispatch, signal }) => {
    const currentState = getState();
    const category = getSelectedCategory(currentState);
    const currentPosition = selectCurrentLocation(currentState);

    if (!category) {
      rejectWithValue('No category found');
      return;
    }

    try {
      const { data } = await api.searchMarketplaceListings(
        {
          categoryId: category.id,
          latitude: currentPosition.latitude,
          longitude: currentPosition.longitude,
        },
        {
          signal,
        },
      );

      const categories = selectAllMarketplaceCategories(getState());
      const marketplaceListingItems = data.map((item) =>
        transformListingItemToMarketplaceListingItem(item, categories),
      );

      signal.throwIfAborted();
      dispatch(setMarketplaceSearchResults(marketplaceListingItems));
    } catch (e: any) {
      rejectWithValue(e.message);
    }
  },
);
